import { useCallback, useEffect, useState } from 'react';
import {
  deleteAdminCoreMemoryItem,
  getAdminCoreMemory,
  listAdminCoreMemoryAudits,
  regenerateAdminCoreMemory,
  restoreAdminCoreMemorySnapshot,
  revealAdminCoreMemory,
  type AdminCoreMemoryDetail,
  type AdminCoreMemoryAuditItem,
} from '../../lib/admin-core-memory-api';

export default function AdminCoreMemoryPanel({ accessToken, consumerUserId }: { accessToken: string; consumerUserId: string }) {
  const [detail, setDetail] = useState<AdminCoreMemoryDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [audits, setAudits] = useState<AdminCoreMemoryAuditItem[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [memory, auditItems] = await Promise.all([
        getAdminCoreMemory(accessToken, consumerUserId),
        listAdminCoreMemoryAudits(accessToken, consumerUserId),
      ]);
      setDetail(memory);
      setAudits(auditItems);
    } catch (loadError) {
      setDetail(null);
      setError(loadError instanceof Error ? loadError.message : 'Core memory could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, consumerUserId]);

  useEffect(() => { setExpanded(false); void load(); }, [load]);

  const reason = (prompt: string): string | null => {
    const value = window.prompt(prompt)?.trim();
    return value || null;
  };

  const act = async (operation: () => Promise<void>, success: string) => {
    setBusy(true);
    setError(null);
    try {
      await operation();
      setMessage(success);
      await load();
    } catch (actionError) {
      setError(actionError instanceof Error ? actionError.message : 'Core memory action failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-[22px] border border-fuchsia-300/15 bg-fuchsia-300/[0.04] p-4" aria-label="User core memory">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.24em] text-fuchsia-200/70">Protected Memory</p>
          <h4 className="mt-1 text-sm font-semibold text-white">User Core Memory</h4>
          <p className="mt-1 text-xs text-slate-500">Masked by default. All privileged actions are written as user_core_memory.* audit events.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="rounded-full border border-white/10 px-3 py-2 text-xs text-slate-200" disabled={loading} onClick={() => setExpanded((value) => !value)} type="button">{expanded ? 'Collapse' : 'Inspect'}</button>
          <button className="rounded-full border border-fuchsia-200/20 bg-fuchsia-200/10 px-3 py-2 text-xs text-fuchsia-100" disabled={busy || loading} onClick={() => {
            const why = reason('Reason for regenerating this user core memory:');
            if (why) void act(() => regenerateAdminCoreMemory(accessToken, consumerUserId, why), 'Regeneration queued.');
          }} type="button">Regenerate</button>
        </div>
      </div>
      {loading ? <p className="mt-3 text-xs text-slate-400" role="status">Loading masked memory…</p> : null}
      {error ? <p className="mt-3 rounded-xl border border-rose-400/20 bg-rose-400/10 p-3 text-xs text-rose-100" role="alert">{error}</p> : null}
      {message ? <p className="mt-3 rounded-xl border border-emerald-300/20 bg-emerald-300/10 p-3 text-xs text-emerald-100" role="status">{message}</p> : null}
      {detail ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-white/10 bg-slate-950/40 p-3"><span className="text-[10px] uppercase text-slate-500">Status</span><p className="mt-1 text-sm text-white">{detail.refreshStatus}</p></div>
          <div className="rounded-xl border border-white/10 bg-slate-950/40 p-3"><span className="text-[10px] uppercase text-slate-500">Version</span><p className="mt-1 text-sm text-white">{detail.currentVersion}</p></div>
          <div className="rounded-xl border border-white/10 bg-slate-950/40 p-3"><span className="text-[10px] uppercase text-slate-500">Last success</span><p className="mt-1 text-sm text-white">{formatTime(detail.lastSuccessfulRefreshAt)}</p></div>
        </div>
      ) : null}
      {expanded && detail ? (
        <div className="mt-4 space-y-4">
          <div className="rounded-[18px] border border-white/10 bg-slate-950/50 p-4">
            <div className="flex items-center justify-between gap-3"><p className="text-xs font-semibold text-slate-200">Masked summary</p><button className="rounded-full border border-amber-200/20 px-3 py-1.5 text-[10px] text-amber-100" disabled={busy} onClick={() => {
              const why = reason('Reason for viewing sensitive memory in plain text:');
              if (!why) return;
              setBusy(true);
              void revealAdminCoreMemory(accessToken, consumerUserId, why).then((revealed) => {
                setDetail(revealed);
                return listAdminCoreMemoryAudits(accessToken, consumerUserId).then(setAudits);
              }).catch((viewError) => setError(viewError instanceof Error ? viewError.message : 'Sensitive view failed.')).finally(() => setBusy(false));
            }} type="button">Sensitive view</button></div>
            <p className="mt-3 whitespace-pre-wrap text-xs leading-6 text-slate-300">{detail.summaryText || 'No summary generated.'}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-200">Memory items ({detail.items.length})</p>
            <div className="mt-2 space-y-2">
              {detail.items.map((item) => (
                <article className="rounded-[16px] border border-white/10 bg-white/[0.03] p-3" key={item.id}>
                  <div className="flex flex-wrap items-center justify-between gap-2"><div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-wide text-slate-400"><span>{item.category}</span><span>{item.domain || 'general'}</span><span>{item.sensitivity}</span>{item.masked ? <span className="text-amber-200">masked</span> : null}</div><button className="text-[10px] text-rose-200" disabled={busy} onClick={() => {
                    if (!window.confirm('Soft-delete this core memory item?')) return;
                    const why = reason('Reason for deleting this memory item:');
                    if (why) void act(() => deleteAdminCoreMemoryItem(accessToken, consumerUserId, item.id, why), 'Memory item deleted; summary refresh queued.');
                  }} type="button">Delete</button></div>
                  <p className="mt-2 whitespace-pre-wrap text-xs leading-5 text-slate-300">{item.content}</p>
                  <p className="mt-2 text-[10px] text-slate-500">{item.sourceKind} · {formatTime(item.updatedAt)} · {item.status}</p>
                </article>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-200">Successful snapshots ({detail.snapshots.length})</p>
            <div className="mt-2 grid gap-2 md:grid-cols-2">
              {detail.snapshots.map((snapshot) => <article className="rounded-[16px] border border-white/10 bg-white/[0.03] p-3" key={snapshot.id}><div className="flex items-center justify-between gap-2"><p className="text-xs text-white">Version {snapshot.version}</p><button className="text-[10px] text-cyan-100" disabled={busy} onClick={() => {
                const why = reason(`Reason for restoring version ${snapshot.version}:`);
                if (why) void act(() => restoreAdminCoreMemorySnapshot(accessToken, consumerUserId, snapshot.id, why), `Version ${snapshot.version} restored.`);
              }} type="button">Restore</button></div><p className="mt-2 line-clamp-3 text-[11px] leading-5 text-slate-400">{snapshot.summaryText}</p><p className="mt-2 text-[10px] text-slate-600">{formatTime(snapshot.createdAt)} · {snapshot.reason}</p></article>)}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-200">Audit trail ({audits.length})</p>
            <div className="mt-2 space-y-2">
              {audits.map((audit) => <article className="flex flex-wrap items-center justify-between gap-2 rounded-[14px] border border-white/10 bg-white/[0.03] p-3" key={audit.id}><div><p className="text-xs text-slate-200">{audit.action}</p><p className="mt-1 text-[10px] text-slate-500">actor {audit.actorUserId ?? 'system'} · {audit.targetType}</p></div><div className="text-right"><p className="text-[10px] text-slate-400">{formatTime(audit.createdAt)}</p>{typeof audit.metadata.reason === 'string' ? <p className="mt-1 text-[10px] text-slate-500">{audit.metadata.reason}</p> : null}</div></article>)}
              {!audits.length ? <p className="rounded-[14px] border border-dashed border-white/10 p-3 text-xs text-slate-500">No core-memory audit events in the latest 100 admin actions.</p> : null}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function formatTime(value: string | null): string {
  if (!value) return 'N/A';
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(timestamp)) : value;
}
